
import { useState, useEffect } from 'react';
import { useDatabase } from '@/context/DatabaseContext';
import { AssessmentQuestion, AssessmentResponse } from '@/types/assessment';

const STUDY_ABROAD_QUESTION_ID = 63;
const NOT_INTERESTED_OPTION = "Not interested";
const PREFERRED_CAREER_1_ID = 1001;
const PREFERRED_CAREER_2_ID = 1002;
const PREFERRED_CAREER_3_ID = 1003;

export const useAssessmentQuestions = (
  sessionChecked: boolean, 
  activeUserId: string | null, 
  initError: string | null,
  setIsInitializing: (isInitializing: boolean) => void
) => {
  const { getQuestions } = useDatabase();
  const [questions, setQuestions] = useState<AssessmentQuestion[]>([]);
  const [responses, setResponses] = useState<Record<number, string>>({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isQuestionVisible, setIsQuestionVisible] = useState(true);
  const [direction, setDirection] = useState<'next' | 'prev'>('next');
  const [canEarlySubmit, setCanEarlySubmit] = useState(false);

  // Load questions and saved responses
  useEffect(() => {
    if (sessionChecked) {
      try {
        if (!initError) {
          const allQuestions = getQuestions();
          
          if (!allQuestions || allQuestions.length === 0) {
            console.error('useAssessmentState: No questions returned');
            setIsInitializing(false);
            return;
          }
          
          setQuestions(allQuestions);
          
          const userId = activeUserId || 'anonymous';
          console.log('useAssessmentState: Initializing questions for user ID:', userId);
          
          const savedResponses = localStorage.getItem(`responses_${userId}`);
          if (savedResponses) {
            try {
              const parsed = JSON.parse(savedResponses);
              const responseMap: Record<number, string> = {};
              parsed.forEach((item: AssessmentResponse) => {
                responseMap[item.questionId] = item.answer;
              });
              setResponses(responseMap);
              console.log('useAssessmentState: Loaded saved responses');
            } catch (error) {
              console.error('Error parsing saved responses:', error);
            }
          }
          
          const preferredCareers = localStorage.getItem('preferred_careers');
          if (preferredCareers) {
            try {
              const parsedCareers = JSON.parse(preferredCareers);
              const responsesCopy = { ...responses };
              
              if (parsedCareers.length > 0 && parsedCareers[0] !== null) {
                responsesCopy[PREFERRED_CAREER_1_ID] = parsedCareers[0].toString();
              }
              if (parsedCareers.length > 1 && parsedCareers[1] !== null) {
                responsesCopy[PREFERRED_CAREER_2_ID] = parsedCareers[1].toString();
              }
              if (parsedCareers.length > 2 && parsedCareers[2] !== null) {
                responsesCopy[PREFERRED_CAREER_3_ID] = parsedCareers[2].toString();
              }
              
              setResponses(responsesCopy);
              console.log('useAssessmentState: Loaded preferred careers');
            } catch (error) {
              console.error('Error parsing preferred careers:', error);
            }
          }
        }
      } catch (error) {
        console.error('Error initializing assessment:', error);
      } finally {
        setIsInitializing(false);
      }
    }
  }, [sessionChecked, activeUserId, getQuestions, initError, setIsInitializing, responses]);

  // Check if early submission is possible
  useEffect(() => {
    if (questions.length > 0 && responses[STUDY_ABROAD_QUESTION_ID] === NOT_INTERESTED_OPTION) {
      const allPreviousQuestionsAnswered = questions
        .filter(q => q.id < STUDY_ABROAD_QUESTION_ID)
        .every(q => !!responses[q.id]);
      
      setCanEarlySubmit(allPreviousQuestionsAnswered);
    } else {
      setCanEarlySubmit(false);
    }
  }, [questions, responses]);

  const handleOptionSelect = (option: string) => {
    if (!questions.length || currentQuestionIndex >= questions.length) {
      console.error('Cannot select option - invalid question state');
      return;
    }
    
    setResponses(prev => ({
      ...prev,
      [questions[currentQuestionIndex].id]: option
    }));
    
    if (questions[currentQuestionIndex].id === STUDY_ABROAD_QUESTION_ID && option === NOT_INTERESTED_OPTION) {
      const allPreviousQuestionsAnswered = questions
        .filter(q => q.id < STUDY_ABROAD_QUESTION_ID)
        .every(q => !!responses[q.id]);
      
      setCanEarlySubmit(allPreviousQuestionsAnswered);
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setDirection('next');
      setIsQuestionVisible(false);
      
      setTimeout(() => {
        setCurrentQuestionIndex(prevIndex => prevIndex + 1);
        setIsQuestionVisible(true);
      }, 300);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setDirection('prev');
      setIsQuestionVisible(false);
      
      setTimeout(() => {
        setCurrentQuestionIndex(prevIndex => prevIndex - 1);
        setIsQuestionVisible(true);
      }, 300);
    }
  };

  return {
    questions,
    currentQuestionIndex,
    responses,
    isQuestionVisible,
    direction,
    canEarlySubmit,
    handleOptionSelect,
    handleNext,
    handlePrevious
  };
};
